import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { toast } from 'react-toastify';
import { FaExclamationTriangle } from 'react-icons/fa';
import { MPPageTitle } from '../../../../components/ui';
import { getLocalCajaManagementByLocalId } from '../../../../crud/management_api/locales_caja_management';
import { getAllPendings } from '../../../../crud/management_api/local_sessions';
import IndividualCaja from './components/IndividualCaja/IndividualCaja';
import HistorialMovimientos from './components/HistorialMovimientos/HistorialMovimientos';
import PendingCash from './components/PendingCash/PendingCash';
import Arqueos from './components/Arqueos/Arqueos';
import { useLocation } from 'react-router-dom';
import { LocalSession } from '../../../../types/LocalSession';

interface Caja {
  workstation_id: number;
  workstation_name: string;
  last_modified: string;
  last_opened: string;
  last_closed: string;
  user_id: number;
  user_name: string;
  is_open: boolean;
  initial_amount: number;
  current_amount: number;
  final_amount: number;
}

interface CajasManagementProps {
  lastSession?: LocalSession | null;
}

const CajasManagement: React.FC<CajasManagementProps> = ({ lastSession }) => {
  const [showHistorialMovimientos, setShowHistorialMovimientos] = useState(false);
  const [showArqueos, setShowArqueos] = useState(false);
  const [showCajas, setShowCajas] = useState(true);
  const [loading, setLoading] = useState(false);
  const [cajas, setCajas] = useState<Caja[]>([]);
  const [pendings, setPendings] = useState<any | null>(null);

  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'arqueos'){
      setShowArqueos(true);
      setShowCajas(false);
      setShowHistorialMovimientos(false);
    }
  },[location.search]);
  
  // Fetch pendings when lastSession changes (only in this component)
  useEffect(() => {
    if (!lastSession) {
      setPendings(null);
      return;
    }

    getAllPendings(lastSession.local_session_id)
      .then((response) => {
        if (response && response.data) {
          setPendings(response.data);
        } else {
          setPendings(null);
        }
      })
      .catch((error) => {
        console.error('Error obteniendo pendientes:', error);
        setPendings(null);
      });
  }, [lastSession]);

  useEffect(() => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = Number(user.local_id) || 0;


    getLocalCajaManagementByLocalId(localId)
      .then((response) => {
        setCajas(response.data);
      })
      .catch((error) => {
        console.error('Error getting cajas: ', error);
        toast.error('Error obteniendo cajas');
      }).finally(() => {
        setLoading(false);
      }); 
  }, []);

  const refreshCajas = async () => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = Number(user.local_id) || 0;
    
    try {
      const response = await getLocalCajaManagementByLocalId(localId);
      setCajas(response.data);
    } catch (error) {
      console.error('Error getting cajas: ', error);
      toast.error('Error obteniendo cajas');
    }
  };

  function handleShowCajas() {
    setShowCajas(true);
    setShowHistorialMovimientos(false);
    setShowArqueos(false);
  }

  function handleShowHistorialMovimientos() {
    setShowCajas(false);
    setShowHistorialMovimientos(true);
    setShowArqueos(false);
  }

  

  function handleShowArqueos() {
    setShowCajas(false);
    setShowHistorialMovimientos(false);
    setShowArqueos(true);
  }

  // Render del header con 3 botones siempre visibles
  const renderHeader = () => {
    const dynamicTitle = showCajas
      ? 'Cajas'
      : showHistorialMovimientos
      ? 'Cajas-Historial Movimientos'
      : showArqueos
      ? 'Cajas-Arqueos'
      : '';
    const [mainTitle, subTitle] = dynamicTitle.split('-');

    return (
      <div>
        <MPPageTitle subtitle={subTitle}>{mainTitle}</MPPageTitle>
        <div className="flex justify-center mt-4 mb-2">
          <div className="mp-tab-group">
            <button className={`mp-tab ${showCajas ? 'mp-tab-active' : ''}`} onClick={handleShowCajas}>Cajas</button>
            <button className={`mp-tab ${showHistorialMovimientos ? 'mp-tab-active' : ''}`} onClick={handleShowHistorialMovimientos}>Historial movimientos</button>
            <button className={`mp-tab ${showArqueos ? 'mp-tab-active' : ''}`} onClick={handleShowArqueos}>Arqueos</button>
          </div>
        </div>
      </div>
    );
  };

  const renderCajas = () => {
    // Contar cuántas cajas están abiertas
    const openCajasCount = cajas.filter(c => c.is_open).length;
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 py-5">
        {cajas.map((caja, idx) => (
          <IndividualCaja
            key={idx}
            caja={caja}
            cajas={cajas}
            setCajas={setCajas}
            isSessionActive={lastSession?.active || false}
            hasPendientes={
              !!(pendings && (pendings?.pending_pedidos?.length > 0 || pendings?.pending_receipts?.length > 0))
            }
            openCajasCount={openCajasCount}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <LoadingSpinner text="Cargando información de las cajas..." transparent />;
  }

  // Compute if blocking pendings exist (pedidos or receipts) - arqueo excluded
  const hasBlockingPendings = !!(pendings && ((pendings.pending_pedidos?.length ?? 0) > 0 || (pendings.pending_receipts?.length ?? 0) > 0));
  const pendingPedidosCount = pendings?.pending_pedidos?.length ?? 0;
  const pendingReceiptsCount = pendings?.pending_receipts?.length ?? 0;
  const pendingParts: string[] = [];
  if (pendingPedidosCount > 0) {
    pendingParts.push(`${pendingPedidosCount} ${pendingPedidosCount === 1 ? 'pedido' : 'pedidos'}`);
  }
  if (pendingReceiptsCount > 0) {
    pendingParts.push(`${pendingReceiptsCount} ${pendingReceiptsCount === 1 ? 'recibo' : 'recibos'}`);
  }
  const pendingSummary = pendingParts.join(' y ');


  return (
    <div className="px-4 lg:px-6 pt-4">
      {renderHeader()}
      {hasBlockingPendings && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 mt-3" style={{ color: 'var(--text-color)' }}>
          <FaExclamationTriangle className="text-amber-500 flex-shrink-0" />
          <div className="text-sm">
            <strong>Pendientes:</strong> {pendingSummary} pendientes. No es posible cerrar cajas mientras existan estos pendientes.
          </div>
        </div>
      )}
      {showHistorialMovimientos && <HistorialMovimientos lastSession={lastSession} />}
      {showCajas && (
        <>
          <PendingCash cajas={cajas} setCajas={setCajas} refreshCajas={refreshCajas} />
          {renderCajas()}
        </>
      )}
      {showArqueos && <Arqueos lastSession={lastSession} />}
    </div>
  );
};

export default CajasManagement;
